import React from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, AlertTriangle, Calendar, ArrowRight, Heart, Scale, Shield } from 'lucide-react';
import Newsletter from '../components/Newsletter';

const newsArticles = [
  {
    id: 1,
    type: 'memorial',
    title: 'In Memory of Alex Pretti (1989-2026)',
    subtitle: 'An ICU nurse who gave his life protecting others — Rest in Power',
    date: 'January 24, 2026',
    image: null,
    summary: 'Alex Pretti, a 37-year-old U.S. citizen and ICU nurse at the Minneapolis VA hospital, was shot and killed by U.S. Border Patrol agents on January 24, 2026, at the intersection of 26th Street and Nicollet Avenue in south Minneapolis\' Whittier neighborhood.',
    content: `Alex was documenting a federal immigration enforcement operation when Border Patrol agents fired at least ten shots over five seconds, striking him multiple times. Video footage shows Pretti standing in the street holding up his phone to document the agents, with his other hand appearing empty.

Alex was a lawful gun owner with a Minnesota carry permit and had no criminal record. He had previously protested the January 7 fatal shooting of Renée Good by federal officers less than two miles away—near the site of George Floyd's 2020 killing.

His parents described him as a "kindhearted soul" who cared for family, friends, and veterans, stating his final act was to "protect a woman" and calling him a hero.

A U.S. District Court judge has issued a temporary restraining order barring DHS from altering or destroying evidence. Minnesota AG Keith Ellison is fighting to end Operation Metro Surge, while state officials have sued in federal court for access to the scene, agents' identities, and evidence after federal agents blocked BCA investigators despite a search warrant.

Minneapolis Police Chief Brian O'Hara noted this as the third federal agent-involved shooting in under three weeks, with two being homicides in 2026.

We honor Alex's memory and his courage in standing up to protect his community. His sacrifice will never be forgotten.`,
    sources: [
      { name: 'Wikipedia - Killing of Alex Pretti', url: 'https://en.wikipedia.org/wiki/Killing_of_Alex_Pretti' },
      { name: 'CBS Minnesota', url: 'https://www.cbsnews.com/minnesota/live-updates/reported-shooting-south-minneapolis-federal-agents-protesters/' },
      { name: 'Axios Twin Cities', url: 'https://www.axios.com/local/twin-cities/2026/01/25/minnesota-rare-legal-steps-investigation-alex-pretti-shooting' }
    ],
    featured: true
  },
  {
    id: 2,
    type: 'memorial',
    title: 'In Memory of Renee Nicole Good (1988-2026)',
    subtitle: 'A life taken too soon — honoring her memory and demanding justice',
    date: 'January 7, 2026',
    image: null,
    summary: 'Renee Nicole Good, a 37-year-old U.S. citizen, was fatally shot by ICE agent Jonathan Ross on January 7, 2026, in Minneapolis, Minnesota. She was in her car during snowy conditions near a dual-language elementary school where community members had gathered to protect their neighbors from ICE activity.',
    content: `Renee sustained four gunshot wounds and was pronounced dead at Hennepin County Medical Center approximately 45 minutes after the shooting. Witnesses at the scene reported that an ICE agent threw a tear gas grenade under a vehicle before the shooting occurred, contradicting official statements from DHS Secretary Kristi Noem.

Journalist Jake Tapper publicly disputed Noem's account, stating "That's not what happened. We all saw what happened."

Renee's family is exploring legal action against ICE. Her death has sparked protests across Minneapolis and renewed calls for accountability in immigration enforcement.

We honor Renee's memory and stand in solidarity with her family. Her death represents the human cost of aggressive federal enforcement tactics in our communities.`,
    sources: [
      { name: 'Wikipedia - Killing of Renee Good', url: 'https://en.wikipedia.org/wiki/Killing_of_Renee_Good' },
      { name: 'American Immigration Council', url: 'https://www.americanimmigrationcouncil.org/blog/can-renee-good-family-sue-ice-shooting/' }
    ],
    featured: true
  },
  {
    id: 3,
    type: 'legal',
    title: 'Minnesota AG Fights to End Operation Metro Surge',
    subtitle: 'State takes unprecedented legal action against federal immigration enforcement',
    date: 'January 2026',
    summary: 'Minnesota Attorney General Keith Ellison is arguing in court to end Operation Metro Surge following the deaths of two U.S. citizens at the hands of federal agents. State officials have sued in federal court for access to crime scenes, agents\' identities, and evidence.',
    content: `In what officials call "uncharted territory," the state of Minnesota has taken rare legal steps to investigate federal agent-involved shootings after federal agents blocked BCA investigators despite having a valid search warrant.

A U.S. District Court judge has issued a temporary restraining order barring DHS from altering or destroying evidence related to the shootings of Renee Good and Alex Pretti.

The ACLU has also filed a lawsuit in U.S. District Court in Minnesota alleging that ICE and Border Patrol agents routinely stop people without reasonable suspicion, arrest individuals without warrants, and detain U.S. citizens based on race or perceived ethnicity.

One case involves Mubashir Khalif Hussen, a 20-year-old Minneapolis resident of Somali descent, who alleges he was taken by masked agents during his lunch break, forced to the ground, handcuffed, had a knee pressed into his neck, and was driven around in an SUV before being released in a Walmart parking lot.

Minneapolis Mayor Jacob Frey stated that ICE had shown "consistent unconstitutional practice...discriminating only on the basis of are you Latino, are you Somali."`,
    sources: [
      { name: 'Axios Twin Cities', url: 'https://www.axios.com/local/twin-cities/2026/01/25/minnesota-rare-legal-steps-investigation-alex-pretti-shooting' },
      { name: 'Star Tribune', url: 'https://www.startribune.com/aclu-sues-feds-over-alleged-racial-profiling-in-twin-cities-immigration-surge/601564707' }
    ],
    featured: false
  },
  {
    id: 4,
    type: 'alert',
    title: 'Three Federal Agent Shootings in Under Three Weeks',
    subtitle: 'Minneapolis Police Chief calls attention to unprecedented violence',
    date: 'January 2026',
    summary: 'Minneapolis Police Chief Brian O\'Hara has noted that Alex Pretti\'s death marks the third federal agent-involved shooting in under three weeks, with two being homicides in 2026. The pattern of violence has sparked nationwide outrage.',
    content: `The deadly pattern began on January 7 with the killing of Renee Good, followed by the fatal shooting of Alex Pretti on January 24. Both were U.S. citizens exercising their constitutional rights.

Governor Tim Walz has characterized Operation Metro Surge as "a campaign of organized brutality" carried out by "undertrained ICE agents."

Local law enforcement agencies have distanced themselves from ICE operations, with Minneapolis Police declining to participate in immigration enforcement activities.

Protests have erupted across Minneapolis, with crowds chanting "ICE out now" and blocking streets in response to the killings.`,
    sources: [
      { name: 'CBS Minnesota', url: 'https://www.cbsnews.com/minnesota/live-updates/reported-shooting-south-minneapolis-federal-agents-protesters/' }
    ],
    featured: false
  },
  {
    id: 5,
    type: 'alert',
    title: 'Native Americans Targeted by ICE in Minneapolis',
    subtitle: 'Tribal members detained near Little Earth neighborhood',
    date: 'January 8, 2026',
    summary: 'Four Native American men from the Oglala Sioux Tribe were detained by ICE agents under a bridge near the Little Earth neighborhood, a historically Native residential area in south Minneapolis. At least five Native Americans have been arrested during Operation Metro Surge.',
    content: `Tribal leadership reported the detentions on January 8, with at least one person released after approximately 12 hours while the whereabouts of others remained initially unclear.

The targeting of Native American communities represents a disturbing expansion of aggressive ICE tactics beyond immigrant communities to include Indigenous peoples who have lived on this land since time immemorial.

Minnesota Governor Tim Walz characterized the operation as "a campaign of organized brutality" and criticized "undertrained ICE agents" for breaking windows, pulling over vehicles, and dragging pregnant women down streets, including U.S. citizens.`,
    sources: [
      { name: 'Wikipedia - Operation Metro Surge', url: 'https://en.wikipedia.org/wiki/Operation_Metro_Surge' }
    ],
    featured: false
  },
  {
    id: 6,
    type: 'investigation',
    title: 'Operation Metro Surge: Documented Abuses and Excessive Force',
    subtitle: 'Allegations of tackling, pepper spray, and denial of medical care',
    date: 'January 2026',
    summary: 'The ACLU complaint documents numerous allegations of excessive force by federal agents, including tackling, tight handcuffing, pepper spray, and physical restraint. One woman with a pending asylum application was detained without access to necessary diabetes medication.',
    content: `The documented abuses include:

• Tackling and physical assault of detained individuals
• Tight handcuffing causing injury
• Use of pepper spray
• Denial of necessary medical care and medication
• Detention of U.S. citizens without cause
• Racial profiling based on appearance and accent
• Two U.S. citizens shot and killed by federal agents

These practices violate constitutional protections and represent a systematic pattern of civil and human rights abuses that demand federal investigation and accountability.`,
    sources: [
      { name: 'Star Tribune', url: 'https://www.startribune.com/aclu-sues-feds-over-alleged-racial-profiling-in-twin-cities-immigration-surge/601564707' }
    ],
    featured: false
  }
];

const getTypeIcon = (type) => {
  switch (type) {
    case 'memorial':
      return <Heart className="text-red-500" size={20} />;
    case 'legal':
      return <Scale className="text-teal-600" size={20} />;
    case 'alert':
      return <AlertTriangle className="text-amber-500" size={20} />;
    case 'investigation':
      return <Shield className="text-stone-600" size={20} />;
    default:
      return <Calendar className="text-stone-500" size={20} />;
  }
};

const getTypeBadge = (type) => {
  const badges = {
    memorial: { bg: 'bg-red-100', text: 'text-red-700', label: 'Memorial' },
    legal: { bg: 'bg-teal-100', text: 'text-teal-700', label: 'Legal Action' },
    alert: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Community Alert' },
    investigation: { bg: 'bg-stone-100', text: 'text-stone-700', label: 'Investigation' },
    statement: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Official Statement' }
  };
  return badges[type] || badges.statement;
};

const News = () => {
  const featuredArticles = newsArticles.filter(a => a.featured);
  const otherArticles = newsArticles.filter(a => !a.featured);

  return (
    <div className="bg-stone-50 pt-20">
      {/* Hero Section */}
      <section className="relative py-16 bg-gradient-to-b from-amber-50 to-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-100 rounded-full text-red-700 text-sm font-medium mb-6">
              <AlertTriangle size={16} />
              <span>Civil Rights News</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-stone-800 mb-6">
              News & <span className="text-teal-600">Publications</span>
            </h1>
            <p className="text-xl text-stone-600 max-w-3xl mx-auto">
              Tracking civil rights and human rights violations in Minneapolis. 
              Documenting ICE and DHS actions affecting our communities.
            </p>
          </div>
        </div>
      </section>

      {/* Memorial Banner */}
      <section className="py-8 bg-stone-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 pb-4 border-b border-stone-700">
              <div className="flex items-center gap-4">
                <Heart className="text-red-400 flex-shrink-0" size={28} />
                <div>
                  <p className="text-white font-bold text-lg">In Loving Memory of Alex Pretti</p>
                  <p className="text-stone-300 text-sm">1989 - January 24, 2026 • ICU Nurse & Hero • Rest in Power</p>
                </div>
              </div>
              <a 
                href="#memorial-alex" 
                className="px-6 py-2 bg-red-500 hover:bg-red-600 text-white font-medium rounded-lg transition-colors text-sm"
              >
                Read His Story
              </a>
            </div>
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <Heart className="text-red-400 flex-shrink-0" size={28} />
                <div>
                  <p className="text-white font-bold text-lg">In Loving Memory of Renee Nicole Good</p>
                  <p className="text-stone-300 text-sm">1988 - January 7, 2026 • Rest in Power</p>
                </div>
              </div>
              <a 
                href="#memorial-renee" 
                className="px-6 py-2 bg-red-500 hover:bg-red-600 text-white font-medium rounded-lg transition-colors text-sm"
              >
                Read Her Story
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Stories */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-stone-800 mb-8">Featured Stories</h2>
          
          <div className="grid lg:grid-cols-2 gap-8">
            {featuredArticles.map((article) => {
              const badge = getTypeBadge(article.type);
              return (
                <article 
                  key={article.id} 
                  id={article.id === 1 ? 'memorial-alex' : article.id === 2 ? 'memorial-renee' : undefined}
                  className={`p-8 rounded-2xl border ${
                    article.type === 'memorial' 
                      ? 'bg-gradient-to-br from-stone-50 to-red-50 border-red-200' 
                      : 'bg-stone-50 border-stone-200'
                  }`}
                >
                  <div className="flex items-center gap-3 mb-4">
                    {getTypeIcon(article.type)}
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
                      {badge.label}
                    </span>
                    <span className="text-stone-500 text-sm">{article.date}</span>
                  </div>
                  
                  <h3 className={`text-2xl font-bold mb-2 ${
                    article.type === 'memorial' ? 'text-stone-800' : 'text-stone-800'
                  }`}>
                    {article.title}
                  </h3>
                  <p className="text-stone-600 font-medium mb-4">{article.subtitle}</p>
                  
                  <p className="text-stone-600 mb-4 leading-relaxed">{article.summary}</p>
                  
                  <div className="prose prose-stone max-w-none mb-6">
                    {article.content.split('\n\n').map((paragraph, idx) => (
                      <p key={idx} className="text-stone-600 mb-3 leading-relaxed text-sm">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                  
                  {article.sources && (
                    <div className="pt-4 border-t border-stone-200">
                      <p className="text-xs font-medium text-stone-500 mb-2">Sources:</p>
                      <div className="flex flex-wrap gap-2">
                        {article.sources.map((source, idx) => (
                          <a
                            key={idx}
                            href={source.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-teal-600 hover:text-teal-700"
                          >
                            {source.name}
                            <ExternalLink size={12} />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Other News */}
      <section className="py-16 bg-amber-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-stone-800 mb-8">More Coverage</h2>
          
          <div className="space-y-6">
            {otherArticles.map((article) => {
              const badge = getTypeBadge(article.type);
              return (
                <article 
                  key={article.id}
                  className="p-6 bg-white rounded-xl border border-stone-200 shadow-sm"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 mt-1">
                      {getTypeIcon(article.type)}
                    </div>
                    <div className="flex-grow">
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
                          {badge.label}
                        </span>
                        <span className="text-stone-500 text-sm">{article.date}</span>
                      </div>
                      
                      <h3 className="text-xl font-bold text-stone-800 mb-1">{article.title}</h3>
                      <p className="text-stone-600 font-medium text-sm mb-3">{article.subtitle}</p>
                      <p className="text-stone-600 text-sm leading-relaxed mb-4">{article.summary}</p>
                      
                      <details className="group">
                        <summary className="cursor-pointer text-teal-600 hover:text-teal-700 font-medium text-sm inline-flex items-center gap-1">
                          Read more
                          <ArrowRight size={14} className="group-open:rotate-90 transition-transform" />
                        </summary>
                        <div className="mt-4 pt-4 border-t border-stone-100">
                          {article.content.split('\n\n').map((paragraph, idx) => (
                            <p key={idx} className="text-stone-600 mb-3 leading-relaxed text-sm">
                              {paragraph}
                            </p>
                          ))}
                          {article.sources && (
                            <div className="pt-4 mt-4 border-t border-stone-100">
                              <p className="text-xs font-medium text-stone-500 mb-2">Sources:</p>
                              <div className="flex flex-wrap gap-2">
                                {article.sources.map((source, idx) => (
                                  <a
                                    key={idx}
                                    href={source.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-xs text-teal-600 hover:text-teal-700"
                                  >
                                    {source.name}
                                    <ExternalLink size={12} />
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </details>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Know Your Rights */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="p-8 bg-teal-600 rounded-2xl text-center">
            <Shield className="text-white mx-auto mb-4" size={48} />
            <h2 className="text-3xl font-bold text-white mb-4">Know Your Rights</h2>
            <p className="text-white/90 mb-6 max-w-2xl mx-auto">
              If you witness ICE activity or experience a civil or human rights violation, document everything 
              and report it immediately. You have constitutional rights regardless of immigration status.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="px-8 py-3 bg-white text-teal-600 font-semibold rounded-lg hover:bg-stone-100 transition-colors"
              >
                Report a Violation
              </Link>
              <a
                href="https://www.aclu.org/know-your-rights/immigrants-rights"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-3 bg-teal-700 text-white font-semibold rounded-lg hover:bg-teal-800 transition-colors inline-flex items-center justify-center gap-2"
              >
                ACLU Rights Guide
                <ExternalLink size={16} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <Newsletter />
    </div>
  );
};

export default News;
